import { ReactIcons } from "@kalyzee/escaet-ui";
import { Meeting, MeetingRoom, MeetingSession, MeetingSessionStatus } from "@kalyzee/kast-web-sdk/websocket";
import _ from "lodash";
import React, { ForwardedRef, useCallback, useMemo } from "react";

import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { GridListProps } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { ScreenMediaName } from "../../media/ScreenShare";
import { PlayersGrid } from "../../player/PlayersGrid";
import MeetingPlayerStyleContainer from "../../player/container/MeetingPlayerStyleContainer";
import { PlayerItem } from "../../player/interfaces";

export interface SecondaryScreenGridProps {
  meeting: Meeting;
  sessions: MeetingSession[];
  videoSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[];
  mode?: "sessions" | "groups";
  grid?: Partial<Omit<GridListProps, "data" | "renderItem">>;
  showName?: boolean;
  room?: MeetingRoom;
  hideIfNoVideoSessions?: boolean;
  filter?: (videoSession: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>) => boolean;
  onItems?: (items: PlayerItem[]) => PlayerItem[];
  className?: string;
  style?: React.CSSProperties;
}

export interface SecondaryScreenGridRef {}

export const SecondaryScreenGrid = React.forwardRef(
  (
    {
      meeting,
      sessions,
      videoSessions,
      mode = "sessions",
      grid = {
        row: 2,
        column: 2,
        gap: 8,
        autoAdjust: false,
      },
      showName = true,
      room,
      hideIfNoVideoSessions = false,
      filter,
      onItems,
      className,
      style,
    }: SecondaryScreenGridProps,
    forwardRef: ForwardedRef<SecondaryScreenGridRef>
  ) => {
    const meetingWithDisconnectedSessions = useMemo(() => {
      const result = _.cloneDeep(meeting);
      sessions.forEach((s) => {
        if (!result.sessions?.find((s2) => s2.id === s.id)) {
          // session to display is not connected
          s.status = MeetingSessionStatus.FINISHED; // update status
          result.sessions?.push(s);
        }
      });
      return result;
    }, [meeting, sessions]);

    const sessionsToPlayerData = useCallback(
      (sessions: MeetingSession[], room?: MeetingRoom): PlayerItem[] => {
        const items = sessions
          .map((session) => {
            const currentVideoSessions = videoSessions.filter((s2) => {
              if (filter) return s2.data.session.id === session.id && filter(s2);
              return s2.data.session.id === session.id && s2.data.mediaSession.name !== ScreenMediaName;
            });
            if (hideIfNoVideoSessions && !currentVideoSessions.length) return undefined;
            return new PlayerItem({
              type: "meeting_session",
              meetingSession: session,
              videoSessions: currentVideoSessions,
              extra: {
                isLeaderSession: room?.leaderUserId === session.user?.id,
              },
            });
          })
          .filter(Boolean) as PlayerItem[];
        const ii = onItems ? onItems(items) : items;
        return ii;
      },
      [hideIfNoVideoSessions, videoSessions, filter, onItems]
    );

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //
    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    return (
      <PlayersGrid
        items={sessionsToPlayerData(sessions, room)}
        className={className}
        style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          ...(style ?? {}),
        }}
        meeting={meetingWithDisconnectedSessions}
        gridProps={grid}
        noPages={true}
        renderItem={(renderDefaultPlayer, item) => {
          if (!item) return <div></div>;
          const itemData = item.getData();
          const isLeaderSession = itemData.extra?.isLeaderSession;
          const additionalStyle: React.CSSProperties = {};
          if (mode === "groups") {
            additionalStyle.borderRadius = "3px";
            additionalStyle.overflow = "hidden";
          }
          if (itemData.type === "meeting_session") {
            const render = () => {
              if (showName) {
                return (
                  <MeetingPlayerStyleContainer
                    mode="secondary_screen"
                    meeting={meeting}
                    session={itemData.meetingSession}
                    style={{ width: "100%", height: "100%" }}
                  >
                    {renderDefaultPlayer()}
                  </MeetingPlayerStyleContainer>
                );
              } else {
                return renderDefaultPlayer();
              }
            };
            return (
              <div
                style={{
                  ...additionalStyle,
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                {render()}
                {isLeaderSession && (
                  <ReactIcons.CrownBg
                    style={{
                      width: 40,
                      height: 40,
                      position: "absolute",
                      bottom: 10,
                      left: 10,
                    }}
                  />
                )}
              </div>
            );
          } else {
            return renderDefaultPlayer();
          }
        }}
        defaultRendererProviderOptions={{
          muted: true,
        }}
      />
    );
  }
);
